import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../data";

const CALLOUTS = [
  {
    label: "Glaucoma",
    text: "Subtle nerve-layer changes appear here long before they'd show in a standard exam.",
    range: [0.12, 0.22, 0.36, 0.44],
  },
  {
    label: "Macular Degeneration",
    text: "Cross-sectional imaging measures the tissue thickness that flags early degeneration.",
    range: [0.4, 0.5, 0.62, 0.7],
  },
  {
    label: "Diabetic Eye Changes",
    text: "Fluid and structural shifts inside the retinal layers are caught at their earliest stage.",
    range: [0.66, 0.76, 0.9, 0.98],
  },
];

const Callout = ({ progress, data, i }) => {
  const opacity = useTransform(progress, data.range, [0, 1, 1, 0.25]);
  const x = useTransform(progress, [data.range[0], data.range[1]], [40, 0]);
  return (
    <motion.div
      style={{ opacity, x }}
      className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-6 md:p-7"
      data-testid={`oct-callout-${i}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <span className="text-[#F59E0B] text-xs font-semibold">0{i + 1}</span>
        <h3 className="font-display text-2xl md:text-3xl text-white font-light">{data.label}</h3>
      </div>
      <p className="text-white/70 text-sm md:text-base font-light leading-relaxed">{data.text}</p>
    </motion.div>
  );
};

export default function OctSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["4%", "-4%"]);

  return (
    <section ref={ref} className="relative h-[340vh] bg-[#08080b]" data-testid="oct-section">
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <motion.img
          src={IMAGES.octLayers}
          alt="Retinal layers, exploded"
          style={{ scale: imgScale, y: imgY }}
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08080b] via-[#08080b]/50 to-transparent" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
          <div className="max-w-xl">
            <p className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#F59E0B] mb-4">
              The Layers · OCT
            </p>
            <h2 className="font-display font-light text-white text-3xl md:text-5xl leading-tight mb-4">
              An "optical ultrasound" that sees each layer of the retina.
            </h2>
            <p className="text-white/70 font-light text-sm md:text-base mb-10 max-w-md">
              Many eye diseases begin with subtle changes that aren't visible in a standard photograph.
              A cross-sectional, layered view catches them early:
            </p>
            <div className="space-y-3 md:space-y-4">
              {CALLOUTS.map((c, i) => (
                <Callout key={c.label} progress={scrollYProgress} data={c} i={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
